import React, { useEffect, useRef } from 'react';
import WaveSurfer from 'wavesurfer.js';
import cx from 'classnames';
import { useState } from 'react';
import styles from './Player.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/store';
import Play from 'src/assets/icons/Play';
import Pause from 'src/assets/icons/Pause';
import { throttleFunction } from 'src/utils/commonUtils';
import {
  IGlobalPlayer,
  closePlayer,
  playPause,
  setSecondsElapsedOfGlobalPlayer,
  setCursor,
  setCursorPointer
} from 'src/reducers/commonSlice';
import { Link } from 'react-router-dom';

function dragElement(elmnt: any) {
  let x1 = 0,
    y1 = 0,
    x2 = 0,
    y2 = 0;
  if (document.getElementById(elmnt.id + 'header')) {
    /* if present, the header is where you move the DIV from:*/
    document.getElementById(elmnt.id + 'header').onmousedown = dragMouseDown;
  } else {
    /* otherwise, move the DIV from anywhere inside the DIV:*/
    elmnt.onmousedown = dragMouseDown;
  }

  function dragMouseDown(e) {
    e = e || window.event;
    e.preventDefault();
    // get the mouse cursor position at startup:
    x2 = e.clientX;
    y2 = e.clientY;
    document.onmouseup = closeDragElement;
    // call a function whenever the cursor moves:
    document.onmousemove = elementDrag;
  }

  function elementDrag(e) {
    e = e || window.event;
    e.preventDefault();
    // calculate the new cursor position:
    x1 = x2 - e.clientX;
    y1 = y2 - e.clientY;
    x2 = e.clientX;
    y2 = e.clientY;

    const elHeight = 58;
    const elWidth = 300;

    // set the element's new position:
    const top = elmnt.offsetTop - y1;
    const left = elmnt.offsetLeft - x1;

    elmnt.style.top = top + 'px';
    elmnt.style.left = left + 'px';
  }

  function closeDragElement() {
    /* stop moving when mouse button is released:*/
    document.onmouseup = null;
    document.onmousemove = null;
  }
}

export const secondsToTimestamp = (seconds: number) => {
  seconds = Math.floor(seconds);
  const h: any = Math.floor(seconds / 3600);
  let m: any = Math.floor((seconds - h * 3600) / 60);
  let s: any = seconds - h * 3600 - m * 60;

  m = m < 10 ? (h > 0 ? '0' : '') + m : m;
  s = s < 10 ? '0' + s : s;

  return `${h > 0 ? `${h}:` : ''}${m}:${s}`;
};

const playBackRates = [0.8, 1, 1.15];

const id = '__globalAudopPlayer__';

let GlobalPlayerInstance: any = undefined;
let CurrentPlayingInstance: any = undefined;

export const getGlobalPlayerInstance = () => {
  return GlobalPlayerInstance;
};

export const setCurrentPlayerInstance = (instance: any) => {
  if (!instance) return;
  CurrentPlayingInstance = instance;
};

export const resetCurrentPlayerInstance = () => {
  CurrentPlayingInstance = undefined;
};

export const GlobalPlayer = () => {
  const {
    cursor = 0,
    visible,
    currentPlayerContainerId,
    url,
    caseUrl
  } = useSelector((state: RootState) => state.common.globalPlayer) as IGlobalPlayer;
  const playing = useSelector((state: RootState) => state?.common?.globalPlayer?.status === 'play');
  const status = useSelector((state: RootState) => state?.common?.globalPlayer?.status || '');
  const dispatch = useDispatch();
  // TOD:: Local states to be moved to redux.
  const [loading, setLoading] = useState(true);
  const [secondsElapsed, setSecondsElapsed] = useState(0);
  const [playBackRate, setPlaybackRate] = useState(playBackRates[1]);
  const playerContainerRef = useRef();

  const waveForm = useRef<any>();
  const prevPlayerRef = useRef();

  const addEvents = (waveformInstance: any) => {
    waveformInstance.unAll();

    waveformInstance.on('ready', function () {
      setLoading(false);
      // console.log('ready', cursor);
      waveformInstance.seekTo(cursor);
      if (!waveformInstance.isPlaying()) {
        waveformInstance.play();
      }
    });

    waveformInstance.on('pause', function () {
      dispatch(playPause('pause'));
    });

    waveformInstance.on('play', function () {
      setLoading(false);
      dispatch(playPause('play'));
    });

    waveformInstance.on('seek', function (cursor: number) {
      // dispatch(playPause('play'));
      dispatch(setCursorPointer({ cursor, status }));
    });

    waveformInstance.on('audioprocess', (n: number) => {
      if (CurrentPlayingInstance && !CurrentPlayingInstance.isDestroyed) {
        const position = n / waveformInstance.getDuration();

        // Dont emit seek event
        CurrentPlayingInstance.setDisabledEventEmissions(['seek']);

        CurrentPlayingInstance.seekTo(position);

        dispatch(setSecondsElapsedOfGlobalPlayer(n));

        // Allow all events to be emitted again
        CurrentPlayingInstance.setDisabledEventEmissions([]);
      }

      throttleFunction((n: number) => {
        // console.log("audioprocess", { n });
        setLoading(false);
        setSecondsElapsed(n);
      }, 400)(n);
    });
  };

  useEffect(() => {
    if (!waveForm.current) {
      waveForm.current = WaveSurfer.create({
        barWidth: 0.5,
        barRadius: 0.5,
        barGap: 0.5,
        barMinHeight: 1,
        cursorWidth: 1,
        container: `#${id}`,
        backend: 'WebAudio',
        height: 30,
        width: 240,
        progressColor: '#FE6E00',
        responsive: true,
        waveColor: '#C4C4C4',
        cursorColor: '#fff'
      });

      waveForm.current.setPlaybackRate(playBackRate);

      GlobalPlayerInstance = waveForm.current;
    }

    if (playerContainerRef.current) {
      dragElement(playerContainerRef.current);
    }

    return () => {
      waveForm?.current?.destroy();
    };
  }, []);

  useEffect(() => {
    if (waveForm.current) {
      addEvents(waveForm.current);
    }
  }, [waveForm.current, cursor]);

  useEffect(() => {
    if (waveForm.current && url) {
      setLoading(true);
      waveForm.current.load(url);
    }
  }, [url]);

  useEffect(() => {
    if (!visible) return;
    waveForm?.current?.seekTo(cursor);
    if (!waveForm?.current?.isPlaying()) {
      waveForm?.current?.play();
    }
  }, [cursor]);

  const handlePlaybackRate = (rate: number) => () => {
    // const newPlayback = playBackRate < playBackRates.length - 1 ? playBackRate + 1 : 0;
    setPlaybackRate(rate);
    waveForm.current.setPlaybackRate(rate);
  };

  const handlePlayPause = () => {
    waveForm.current?.playPause();
  };

  const handleClose = () => {
    waveForm.current?.pause();
    dispatch(closePlayer());
  };

  return (
    <div
      ref={playerContainerRef}
      className={styles.player}
      style={{ display: !visible ? 'none' : 'flex' }}
    >
      <div className={styles.playerHover}>
        <div className={styles.playRateWrapper}>
          {playBackRates?.map(rate => (
            <span
              key={rate}
              className={cx(styles.playBackRate, { [styles.active]: rate === playBackRate })}
              onClick={handlePlaybackRate(rate)}
            >
              {rate}x
            </span>
          ))}
          <span onClick={handleClose} className={styles.playBackRate}>
            Close
          </span>
          {caseUrl !== location.href && (
            <Link to={caseUrl} className={cx(styles.playBackRate, styles.caseLink)}>
              Goto case
            </Link>
          )}
        </div>
      </div>

      <div className={styles.controls}>
        <span onClick={handlePlayPause}>{!playing ? <Play /> : <Pause />}</span>
        {/* <span>
          <span className={styles.stop}></span>
        </span> */}
      </div>

      <div id={id} className={styles.waves}></div>

      <div className={styles.timer}>
        <span className={styles.current}>{secondsToTimestamp(secondsElapsed)}</span>/
        <span className={styles.duration}>
          {secondsToTimestamp(waveForm?.current?.getDuration())}
        </span>
      </div>

      {loading && <div className={styles.loading}>Loading...</div>}
    </div>
  );
};
